/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

/**
 * The shared contract between the server (`buildStaticHtml`) and the client
 * (`hydration`). This module is browser-safe — it imports nothing — so the
 * app bundle can pull the constants in without dragging Puppeteer/Express in.
 */

/**
 * Attribute the server stamps on the prerendered app-root element so the
 * client can find and remove exactly it on boot: `data-static-prerender`.
 */
export const PRERENDER_ROOT_ATTR = 'data-static-prerender'

/**
 * Attribute the app stamps on each `<head>` node it owns for SEO (canonical,
 * description, `og:*`, JSON-LD) so the server carries exactly those nodes into
 * the served page: `data-prerender-head`.
 */
export const PRERENDER_HEAD_ATTR = 'data-prerender-head'

/**
 * Attribute the app stamps on its not-found page so the server can answer the
 * request with HTTP 404: `data-prerender-not-found`.
 */
export const PRERENDER_NOT_FOUND_ATTR = 'data-prerender-not-found'

/**
 * The global the served prerendered page publishes (via an inline script the
 * page carries) BEFORE the app module evaluates: a plain
 * `Record<storageKey, string>` mapping each cache-manager storage key to its
 * cached GraphQL response body. The app's `preseedCacheManager` bootstrap reads
 * it and writes those entries back into the CacheManager, so `gqlFetch` resolves
 * a preseeded query through its NORMAL cache read — no fetch-path fast branch.
 *
 * It is produced BY a render, not consumed by one: during a render `gqlFetch`'s
 * ordinary cache-write fills the render's stubbed in-memory cache manager, and
 * the render worker snapshots those entries into this map. The key is the
 * CacheManager's full storage key, so both sides stay agnostic of the manager's
 * internal key prefix. (Seeded via the global rather than a setter because the
 * bundled app can't be handed the app's own module instance — same pattern as
 * {@link STATIC_RENDER_GLOBAL}.)
 */
export const GQL_PRESEED_GLOBAL = '__gqlPreseed'

/**
 * The boolean global the in-Node render sets to `true` BEFORE it imports the
 * app, marking the run as a static prerender (no interactive user, no
 * playback). The app reads it at boot to gate out browser-only media
 * machinery (MSE/EME, the `<video>` element, the vinyl player) that a headless
 * render must never touch. It is never written onto served HTML: a person's
 * browser boots the live app.
 */
export const STATIC_RENDER_GLOBAL = '__staticRender'

/**
 * The global the app publishes DURING a static render: an
 * `ObservableValue<number>` counting `instrument`-wrapped async calls currently
 * in flight (see the app's `core/perf`). The render worker can't reach the
 * bundled app's module instance, so — same seam as {@link STATIC_RENDER_GLOBAL}
 * — the app exposes the counter here (only when booting as a static render) and
 * the worker watches it to detect render quiescence (all in-flight instrumented
 * async settled) instead of waiting a fixed time.
 */
export const PENDING_INSTRUMENTED_GLOBAL = '__pendingInstrumentedCount'

/**
 * Boolean the Node render worker sets (`true`) before the app module runs, so
 * the app treats the invite as already accepted rather than hitting the invite
 * wall. Set ONLY by the in-Node render — never emitted onto served prerendered
 * pages — so a served page's invite wall stays intact.
 */
export const PRERENDER_INVITE_GLOBAL = '__prerenderInvite'

/** Public origin for SEO URLs; the render's `location` must stay on localhost for GraphQL. */
export const PRERENDER_PUBLIC_ORIGIN_GLOBAL = '__prerenderPublicOrigin'
